import { constants } from 'day8-ui';
import {
  STANDARD_YACHT, EXTERNAL_SKIPPER_YACHT_IDS, YACHT_SORTING_OPTIONS
} from '../constants';
import { capitalizeFirstLetter } from './utilities';
import { getQuery } from './querystring';
import { maxBoatCapacityByDestination } from './crew';
import {
  filterYachts, filterYachtsByWeek, sortBy
} from '../actions/yachts';
import { getMaxCapacityForStandardYacht } from './ratings';

const { TYW_DESTINATION } = constants;

export const getBoatRatingFromStandardRating = rating => {
  switch (rating) {
  case STANDARD_YACHT.CLASSIC_RATING:
    return 2;
  case STANDARD_YACHT.PREMIUM_RATING:
    return 3;
  case STANDARD_YACHT.LUXURY_RATING:
    return 4;
  case STANDARD_YACHT.LUXURY_PLUS_RATING:
    return 4;
  default:
    return null;
  }
};

export const yachtCardFromStandardYachtWeek = (standardYachtWeek, week) => {
  const { standardYacht } = standardYachtWeek;
  const images = Object.values(standardYacht.images).map(image => image.path);
  // not adding extra here -> it's just syw price which passed into pricing helper getTotalPrice
  const price =
    standardYachtWeek.price - standardYachtWeek.discount;
  return {
    isStandard: true,
    id: standardYachtWeek.id,
    standardYachtWeekId: standardYachtWeek.id,
    price,
    hullType: standardYacht.hullType,
    size: `${standardYacht.lengthFrom} - ${standardYacht.lengthTo}`,
    cabins: `${standardYacht.minCabins}+`,
    productionYear: `${standardYacht.yearFrom} - ${standardYacht.yearTo}`,
    maxPersons: standardYacht.maxCapacity,
    deposit: 'TBC',
    marina: {
      name: 'Assigned 2 weeks prior to arrival',
      info: ''
    },
    package: standardYacht.package,
    boat: {
      year: `${standardYacht.yearFrom} - ${standardYacht.yearTo}`,
      cabins: `${standardYacht.minCabins}+`,
      size: `${standardYacht.lengthFrom} - ${standardYacht.lengthTo}`,
      capacity: getMaxCapacityForStandardYacht(null, null, week?.destination.id, false, standardYacht.package),
      hullType: standardYacht.hullType,
      type: `${capitalizeFirstLetter(standardYacht.package.name)} Yacht`,
      id: null,
      name: `${capitalizeFirstLetter(standardYacht.package.name)} Yacht`,
      attributes: [1, 2],
      images
    }
  };
};

export const deNormalizeData = data => {
  const { boats, weeks, marinas } = data;
  return data.boatWeeks.map(bw => ({
    ...bw,
    boat: {
      ...boats[bw.boat],
      capacity: maxBoatCapacityByDestination(boats[bw.boat].capacity, weeks[bw.week])
    },
    week: weeks[bw.week],
    marina: marinas[bw.marina]
  }));
};

export const yachtDataLoaded = (dispatch, location, destinations) => {
  const {
    criteria
  } = getQuery(location);

  // let types;
  // let selectedHulls;
  // let selectedAmenities;

  // if (yachtTypes) {
  //   types = Array.isArray(yachtTypes) === false ? [yachtTypes] : yachtTypes;
  //   types = types.map(type => parseInt(type, 10));
  // }

  // if (hulls) {
  //   selectedHulls = Array.isArray(hulls) === false ? [hulls] : hulls;
  // }

  // if (amenities) {
  //   selectedAmenities = Array.isArray(amenities) === false ? [amenities] : amenities;
  // }

  dispatch(filterYachtsByWeek(destinations, location));
  dispatch(filterYachts(location));

  if (criteria) {
    const criteriaObject = YACHT_SORTING_OPTIONS.find(o => o.value === JSON.parse(criteria));
    if (criteriaObject) {
      dispatch(sortBy(criteriaObject, location));
    }
  }

  // if (marina) {
  //   dispatch(selectMarina(marina, location));
  // }
};
// Note: this is not counting on skipper, just decreasing overall number like it would come down modified through API...
export const yachtCapacityWithHost = ({
  doubleCabins = 0,
  bunkCabins = 0,
  skipperCabinSpots = 0,
  saloonSpots = 0
},
actualMaxGuests,
actualCapacity,
destinationId) => {
  // Ignore custom host capacity here
  if (TYW_DESTINATION.TURKEY === destinationId) {
    return ({
      capacity: actualCapacity,
      maxGuests: actualMaxGuests
    });
  }
  const capacity = actualCapacity || (doubleCabins * 2) + (bunkCabins * 2) + (skipperCabinSpots) + saloonSpots;
  if (skipperCabinSpots > 1 || (skipperCabinSpots === 1 && saloonSpots === 1) || (skipperCabinSpots === 1 && bunkCabins > 0) || (skipperCabinSpots === 0 && saloonSpots == 0 && bunkCabins > 0)) {
    return ({
      capacity,
      maxGuests: capacity - 1 // -1 for skipper who is always there
    });
  }
  if (skipperCabinSpots < 1 && bunkCabins < 1 && saloonSpots > 1) {
    return ({
      capacity: capacity - 2, // can happen that there is 0 skipper spots but then double saloon which cannot be shared by skipper and host
      maxGuests: capacity - 3
    });
  }
  return ({
    capacity: capacity - 1, // take away one spot from overall capacity because host now takes one extra spot
    maxGuests: capacity - 2 // if someone selects host it means to take one extra spot so host + 1
  });
};

export const hasExternalSkipper = boatId => EXTERNAL_SKIPPER_YACHT_IDS.includes(boatId);

/**
 * Alt text of a yacht picture: the alt the admin set on the image (carried as boat.imageAlts,
 * aligned with boat.images), else the yacht type, else a plain word. Never an empty alt.
 */
export const yachtImageAlt = (boat, index) => {
  const own = boat && Array.isArray(boat.imageAlts) ? boat.imageAlts[index] : null;
  if (own && String(own).trim() !== '') return String(own).trim();
  return (boat && boat.type) || 'Yacht';
};

/**
 * The yacht's pictures as {url, alt} pairs for galleries; toUrl turns a stored path into a URL.
 */
export const yachtImageEntries = (boat, toUrl = u => u) => (
  ((boat && boat.images) || []).map((img, i) => ({ url: toUrl(img), alt: yachtImageAlt(boat, i) }))
);

/**
 * schema.org ImageObject list for the yacht page head, so search engines index the pictures
 * with their alt text as caption.
 */
export const yachtImageStructuredData = (boat, toUrl = u => u) => ({
  '@context': 'https://schema.org',
  '@graph': yachtImageEntries(boat, toUrl).map(img => ({
    '@type': 'ImageObject',
    contentUrl: img.url,
    name: img.alt,
    caption: img.alt
  }))
});
